import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  Heading,
  List,
  ListItem,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { useFeature } from "@growthbook/growthbook-react";
import { CardBody } from "@paljs/ui/Card";
import { useManualQuery, useMutation, useQuery } from "graphql-hooks";
import _ from "lodash";

import {
  activitiesTermsURL,
  tripSummer2023TermsURL,
  tripWinter2025TermsURL,
  tripWinter2027TermsURL,
} from "../../components/agreements";
import Agreements, {
  hasAllRequiredConsents,
  requiredConsents,
} from "../../components/agreements";
import {
  GenericErrorAlert,
  MissingProfileDataAlert,
} from "../../components/alerts";
import { MembershipSection as MembershipSelectSection } from "../../components/cartItem/subcomponents";
import DiscountCard from "../../components/discounts";
import { EmptyBasket } from "../../components/empty-state";
import Layout from "../../components/layout";
import Loading from "../../components/loading";
import { Card, Col, Row } from "../../components/paljs";
import { missingAccountData, TooltipWrapper } from "../../components/tooltip";
import {
  ADD_DISCOUNT_CODE,
  ADD_MEMBERSHIP_ITEM,
  PLACE_ORDER,
  REMOVE_CART_ITEM,
  REMOVE_DISCOUNT_CODE,
  UPDATE_CHILD,
  UPDATE_CUSTOM_DATA,
  UPDATE_ME,
} from "../../data/mutations";
import {
  GET_CART_PRICE,
  GET_ORDERS,
  GET_PAYMENT_OPTION_ID,
} from "../../data/queries";
import {
  AddMembershipMutation,
  CartItemGroup,
  GetOrdersQuery,
  MembershipType,
  MutationRemoveFromCartArgs,
  OrderPlacementConsentsInput,
  PlaceOrderMutation,
  PlaceOrderMutationVariables,
  UpdateCustomDataMutation,
} from "../../types";
import {
  AdditionalInfoForID,
  checkIfEverybodyHasActivityConsent,
  checkIfEverybodyHasCampConsent,
  checkIfEverybodyHasInsuranceConsent,
  checkIfEverybodyHasLevel,
  checkIfEverybodyHasShirtSize,
  checkIfEverybodyHasSport,
  checkIfEverybodyHasTransport,
  getWinter2027CampsIDsFromItemGroups,
  WINTER_2027_PROFILE,
  getActivitiesIDsFromItemGroups,
  getDynamicName,
  getGenericBundleId,
  getSummerCampsIDsFromItemGroups,
  getWinterCampsIDsFromItemGroups,
  getWinterLevelName,
  // transformAdditionalInfoObjectForMutation,
} from "../../utils/helpers";
import { ADD_SUMMER_CAMPS_2026_TO_CART } from "../summer2026/data";
import { ADD_WINTER_CAMPS_2027_TO_CART } from "../winter2027/data";

import OrderItemActivityRow from "./components/order-item-activity-row";
import OrderItemDiscountRow from "./components/order-item-discount-row";
import OrderItemMembershipRow from "./components/order-item-membership-row";
import OrderItemSummerCampRow from "./components/order-item-summer-camp-row";
import OrderItemWinterCampRow, {
  WinterCampDisclaimer,
} from "./components/order-item-winter-camp-row";

type PaymentTypeInMeQuery = Exclude<
  GetOrdersQuery["me"]["cart"],
  null | undefined
>["paymentOptions"][number]["payments"][number];

const Cart = () => {
  const [isBigScreen] = useMediaQuery("(min-width: 1300px)");
  const dynamicName = getDynamicName(isBigScreen);
  const navigate = useNavigate();

  const [paymentIndex, setPaymentIndex] = useState<number>(0);
  const [paymentActionStarted, setPaymentActionStarted] = useState(false);
  // Blocks a second click from the first click until placeOrder answers (SCRUM-147): a double
  // click used to place the order twice and the customer paid the overwritten payment
  const placingOrderRef = useRef(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false);
  const [hasRequiredConsents, setHasRequiredConsents] = useState(false);
  const [wantsInvoice, setWantsInvoice] = useState(false);
  // Asked on every order (art. 15 of the consumer rights act), never prefilled from the account
  const [serviceStartConsent, setServiceStartConsent] = useState(false);
  const [consents, setConsents] = useState<
    PlaceOrderMutationVariables["input"]["consents"] | Record<string, boolean>
  >();

  const [additionalItemsInfo, setAdditionalItemsInfo] =
    useState<AdditionalInfoForID>({});

  const { loading, data, error } = useQuery<GetOrdersQuery>(GET_ORDERS, {
    refetchAfterMutations: [
      UPDATE_ME,
      UPDATE_CHILD,
      ADD_MEMBERSHIP_ITEM,
      PLACE_ORDER,
      REMOVE_CART_ITEM,
      ADD_DISCOUNT_CODE,
      REMOVE_DISCOUNT_CODE,
      ADD_SUMMER_CAMPS_2026_TO_CART,
      ADD_WINTER_CAMPS_2027_TO_CART,
    ],
    useCache: false,
  });

  const [fetchPayment, manualQueryResponse] = useManualQuery<any>(
    GET_PAYMENT_OPTION_ID
  );

  const { data: cartPriceData } = useQuery<GetOrdersQuery>(GET_CART_PRICE, {
    refetchAfterMutations: [
      UPDATE_CUSTOM_DATA,
      ADD_DISCOUNT_CODE,
      REMOVE_DISCOUNT_CODE,
      ADD_SUMMER_CAMPS_2026_TO_CART,
      ADD_WINTER_CAMPS_2027_TO_CART,
      ADD_MEMBERSHIP_ITEM,
    ],
    useCache: false,
  });

  const [addMembership, { loading: addMembershipLoading }] =
    useMutation<AddMembershipMutation>(ADD_MEMBERSHIP_ITEM);
  const [placeOrder, { loading: placeOrderLoading, data: placeOrderData }] =
    useMutation<PlaceOrderMutation>(PLACE_ORDER);

  const [updateCustomData] =
    useMutation<UpdateCustomDataMutation>(UPDATE_CUSTOM_DATA);

  const [removeCartItem, { loading: removeCartItemLoading }] =
    useMutation<MutationRemoveFromCartArgs>(REMOVE_CART_ITEM);

  const meId = data?.me.id;
  const cart = data?.me.cart;
  const cartProblems = cart?.problems;
  const isIncomplete = data?.me.isIncomplete;
  const itemGroups = _.get(data, "me.cart.itemsGroups");
  const discounts = _.get(cart, "discounts");
  // const hasDiscountCode = _.has(data, "me.cart.discounts.0");
  const currentDiscountCode = _.get(data, "me.cart.discountCodes[0].code");
  const paymentOptions = _.get(
    cartPriceData, // notice different data
    "me.cart.paymentOptions"
  ) as unknown as Array<{
    name: string;
  }>;

  // Mapa cen bundli z cartPriceData (odświeża się po UPDATE_CUSTOM_DATA)
  const bundlePricesMap = _.reduce(
    _.get(cartPriceData, "me.cart.itemsGroups", []),
    (acc: Record<string, any>, itemGroup: any) => {
      _.forEach(itemGroup.bundles, (bundle: any) => {
        let bundleId = bundle.id as string;

        if (!bundle.id) {
          bundleId = getGenericBundleId(bundle);
        }

        acc[bundleId] = bundle;
      });
      return acc;
    },
    {}
  );

  const anyCartLoading =
    loading || addMembershipLoading || removeCartItemLoading;
  const {
    EXPIRED_MEMBERSHIP: expiredMembershipProblems,
    MISSING_ACCOUNT_DATA: missingAccountDataProblems,
    MISSING_CHILD_DATA: missingChildDataProblems,
    MISSING_MEMBERSHIP: missingMembershipProblemsOld,
    ProductOutOfStock: productsOutOfStock,
  } = _.groupBy(cartProblems, "code");

  const missingNextMembershipsProblems = _.filter(cartProblems, (problem) => {
    const isMissingProduct = problem.code === "MissingProductRequirement";
    const propositionIsMembership = _.some(
      _.get(problem, "availableProducts", []),
      ({ name }) => _.startsWith(name, "Członkostwo")
    );
    return isMissingProduct && propositionIsMembership;
  });

  const missingMembershipProblems = [
    ...missingNextMembershipsProblems,
    ...(missingMembershipProblemsOld || []),
  ];

  const setInfoStateForBundle =
    (bundleId: string) => (key: string) => (value: any) => {
      const newAdditionalInfoObject = {
        ...additionalItemsInfo,
        [bundleId]: {
          ...additionalItemsInfo[bundleId],
          [key]: value,
        },
      };
      setAdditionalItemsInfo(newAdditionalInfoObject);

      updateCustomData({
        variables: {
          itemId: bundleId,
          data: { [key]: value },
        },
      });
    };

  const getAdditionalInfoByKeyForBundle = (bundle: any) => (key: string) => {
    const bundleId = bundle.id as string;

    const temporaryAdditionalItem = _.get(
      additionalItemsInfo,
      [bundleId, key],
      null
    );

    return temporaryAdditionalItem;
  };

  useEffect(() => {
    if (loading === false && data) {
      setHasRequiredConsents(hasAllRequiredConsents(data.me.consents));

      const createAdditionalInfoObject = (itemsGroups: any) => {
        return _.reduce(
          itemsGroups,
          (acc, itemGroup) => {
            const bundles = _.get(itemGroup, "bundles", []);
            _.map(bundles, (bundle) => {
              const bundleId = bundle.id as string;
              const customData = bundle.customData;
              if (!_.isEmpty(customData)) {
                acc = { ...acc, [bundleId]: customData };
              }
            });
            return acc;
          },
          {}
        );
      };

      const loadedAdditionalInfo = createAdditionalInfoObject(
        data?.me?.cart?.itemsGroups
      );

      // Merge instead of replacing: activity consents live only in local state (they are never
      // saved on the server), so replacing wiped every participant's consent on each cart refetch
      setAdditionalItemsInfo((previous) => ({
        ...previous,
        ...loadedAdditionalInfo,
      }));
    }
  }, [loading, data]);

  useEffect(() => {
    if (
      loading === false &&
      placeOrderData &&
      placeOrderData.placeOrder.payments[0].link
    ) {
      window.location.replace(`${placeOrderData.placeOrder.payments[0].link}`);
    }
  }, [placeOrderLoading, data, loading, placeOrderData]);

  useEffect(() => {
    const paymentOption = _.get(
      manualQueryResponse,
      `data.me.cart.paymentOptions.${paymentIndex}.id`
    );
    if (paymentOption) {
      const input: PlaceOrderMutationVariables["input"] = {
        paymentOption,
      };
      if (!_.isEmpty(consents) && consents) {
        input.consents = consents as OrderPlacementConsentsInput;
      }
      if (paymentActionStarted) {
        console.log("Payment action start");
        input.wantsInvoice = wantsInvoice;
        input.serviceStartConsent = serviceStartConsent;
        // A re-run of this effect clears the pending call, so at most one placeOrder is sent
        const timer = setTimeout(() => {
          setPaymentActionStarted(false);
          placeOrder({ variables: { input } }).then((result) => {
            // On success the page redirects to P24; on an error the customer may try again
            if (result.error) {
              placingOrderRef.current = false;
              setIsPlacingOrder(false);
            }
          });
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [
    manualQueryResponse,
    paymentIndex,
    placeOrder,
    consents,
    paymentActionStarted,
    wantsInvoice,
    serviceStartConsent,
  ]);

  const addMembershipAction = async (
    accountId: string,
    type: MembershipType,
    childId?: string
  ) => {
    await addMembership({
      variables: { accountId, childId, input: { type, start: "", end: "" } },
    });
  };

  const removeFromCart = async (id: string, bundle: any) => {
    await removeCartItem({ variables: { itemId: bundle.id } });
  };

  // Mirrors the priceModifier of the product definitions (the public API does not expose the
  // product base price), only to show the price without add-ons in the row
  const getPriceForCustomData = (
    key: string,
    value: string | null,
    isWinter2027: boolean
  ) => {
    if (key === "transport") {
      if (value === "busDouble") {
        return isWinter2027 ? 385 : 296;
      } else if (value === "busThereOnly" || value === "busReturnOnly") {
        return isWinter2027 ? 230 : 176;
      }
    } else if (key === "diet" && value !== null && !isWinter2027) {
      return 200;
    }
  };

  const getPriceDetailsForBundle = (bundle: any) => {
    let bundleId = bundle.id as string;

    if (!bundleId) {
      bundleId = getGenericBundleId(bundle);
    }

    // Używamy danych z bundlePricesMap (cartPriceData) które odświeżają się po UPDATE_CUSTOM_DATA
    const priceBundle = bundlePricesMap[bundleId] || bundle;
    const productCustomData = priceBundle.customData || {};
    const customDataPriceBeforeDiscounts = _.reduce(
      productCustomData,
      (acc, value, key) =>
        acc +
        (getPriceForCustomData(
          key,
          value,
          bundle.profile === WINTER_2027_PROFILE
        ) || 0),
      0
    );
    const productDiscounts = _.get(priceBundle, "discounts", []);
    const priceAfterDiscounts = _.get(priceBundle, "price.value", null);

    if (!productDiscounts || _.isEmpty(productDiscounts)) {
      return {
        priceBeforeDiscountsWithoutModifiers:
          priceAfterDiscounts - customDataPriceBeforeDiscounts,
        priceBeforeDiscountsWithModifiers: priceAfterDiscounts,
        priceAfterDiscounts,
        discountsAmount: 0,
        productDiscounts,
      };
    }

    const discountsAmount = _.reduce(
      productDiscounts,
      (acc, discount) => acc + _.get(discount, "discountValue.value", 0),
      0
    );

    const priceBeforeDiscountsWithModifiers =
      priceAfterDiscounts + discountsAmount;
    const priceBeforeDiscountsWithoutModifiers =
      priceBeforeDiscountsWithModifiers - customDataPriceBeforeDiscounts;

    return {
      priceBeforeDiscountsWithoutModifiers,
      priceBeforeDiscountsWithModifiers,
      priceAfterDiscounts,
      discountsAmount,
      productDiscounts,
    };
  };

  const PickedActivitiesSummary = (
    payment: PaymentTypeInMeQuery,
    isEditable = false,
    allPayments: any[] = []
  ) => {
    const [
      membershipsBundles,
      activitiesBundles,
      winterCampBundles,
      summerCampBundles,
    ] = _.reduce(
      payment.bundles,
      (accumulator, bundle) => {
        if (bundle.__typename === "CartItemMembership")
          accumulator[0].push(bundle);
        if (bundle.__typename === "CartItemActivityBundle")
          accumulator[1].push(bundle);
        if (bundle.__typename === "CartItemNewWinterCamp")
          accumulator[2].push(bundle);
        if (bundle.__typename === "CartItemSummerCamp")
          accumulator[3].push(bundle);
        return accumulator;
      },
      [[], [], [], []] as [any[], any[], any[], any]
    );
    // Kwota pozycji w tej płatności liczona przez backend: uwzględnia dopłatę ratalną
    // z konfiguracji produktu i zaokrąglenie danej raty, więc wiersze sumują się do kwoty raty
    const paymentPriceOf = (bundle: any, priceAfterDiscounts: number): string =>
      (bundle.paymentPrice?.value ?? priceAfterDiscounts).toFixed(2);

    const MembershipPart = () => (
      <>
        <Box mb={3}>
          <Text as="b">Członkostwo</Text>
        </Box>
        {_.map(membershipsBundles, (bundle: any, idx) => (
          <Box key={idx}>
            <OrderItemMembershipRow
              type={bundle.membershipType as MembershipType}
              price={bundle.price.value}
              name={dynamicName(bundle)}
            />
          </Box>
        ))}
      </>
    );

    const ActivitiesPart = () => (
      <>
        <Box mb={3}>
          <Text as="b">Wybrane zajęcia</Text>
        </Box>
        {_.map(activitiesBundles, (bundle: any, idx) => {
          const bundleId = getGenericBundleId(bundle);
          const setInfoState = (key: string, value: any) => {
            setAdditionalItemsInfo({
              ...additionalItemsInfo,
              [bundleId]: {
                ...additionalItemsInfo[bundleId],
                [key]: value,
              },
            });
          };
          const getAdditionalInfo = (key: string) =>
            _.get(additionalItemsInfo, [bundleId, key], null);
          const priceDetails = getPriceDetailsForBundle(bundle);

          return (
            <Box key={idx}>
              <OrderItemActivityRow
                agreementName={`${bundle.sport} ${bundle.location}`}
                agreementUrl={activitiesTermsURL}
                sport={bundle.sport}
                type={bundle.type}
                price={
                  isEditable
                    ? priceDetails.priceBeforeDiscountsWithoutModifiers.toFixed(
                        2
                      )
                    : paymentPriceOf(bundle, priceDetails.priceAfterDiscounts)
                }
                location={bundle.location}
                name={dynamicName(bundle)}
                bundle={bundle} //fcuk
                setItemConsent={(consent) =>
                  setInfoState("itemConsent", consent)
                }
                itemConsent={getAdditionalInfo("itemConsent")}
                isEditable={isEditable}
              />
            </Box>
          );
        })}
      </>
    );

    const WinterCampsPart = () => (
      <>
        <Box mb={3}>
          <Text as="b">Wybrane wyjazdy zimowe</Text>
        </Box>
        {_.map(winterCampBundles, (bundle: any, idx) => {
          const bundleId = bundle.id as string;
          const setInfoState = setInfoStateForBundle(bundleId);
          const getAdditionalInfoByKey =
            getAdditionalInfoByKeyForBundle(bundle);
          const priceDetails = getPriceDetailsForBundle(bundle);
          const isOutOfStock = _.some(
            productsOutOfStock,
            (item) => _.get(item, "product.sku") === bundle.sku
          );

          return (
            <Box key={idx}>
              <OrderItemWinterCampRow
                isOutOfStock={isOutOfStock}
                agreementUrl={
                  bundle.profile === WINTER_2027_PROFILE
                    ? tripWinter2027TermsURL
                    : tripWinter2025TermsURL
                }
                setTransport={setInfoState("transport")}
                transport={getAdditionalInfoByKey("transport")}
                setCampConsent={setInfoState("campConsent")}
                campConsent={getAdditionalInfoByKey("campConsent")}
                setInsuranceConsent={setInfoState("insuranceConsent")}
                insuranceConsent={getAdditionalInfoByKey("insuranceConsent")}
                setShirtSize={setInfoState("shirtSize")}
                shirtSize={getAdditionalInfoByKey("shirtSize")}
                isEditable={isEditable}
                discount={priceDetails.productDiscounts[0]}
                removeFromCart={(id: string) => removeFromCart(id, bundle)}
                id={isEditable ? bundle.id : null}
                setSport={setInfoState("sport")}
                sport={getAdditionalInfoByKey("sport")}
                setLevel={setInfoState("level")}
                level={getAdditionalInfoByKey("level")}
                setRoommatesInfo={setInfoState("roommatesInfo")}
                roommatesInfo={getAdditionalInfoByKey("roommatesInfo")}
                setHealthInfo={setInfoState("healthInfo")}
                healthInfo={getAdditionalInfoByKey("healthInfo")}
                profile={bundle.profile}
                location={
                  bundle.profile === WINTER_2027_PROFILE
                    ? "SKI & SNB Camp 2027"
                    : "Zgrupowanie SKI & SNB 2023 "
                }
                price={
                  isEditable
                    ? priceDetails.priceBeforeDiscountsWithoutModifiers.toFixed(
                        2
                      )
                    : paymentPriceOf(bundle, priceDetails.priceAfterDiscounts)
                }
                name={dynamicName(bundle)}
                periodName={bundle.name || "błąd?"}
              />
            </Box>
          );
        })}
        <WinterCampDisclaimer />
      </>
    );

    const SummerCampsPart = () => (
      <>
        <Box mb={3}>
          <Text as="b">Wybrane wyjazdy letnie</Text>
        </Box>
        {_.map(summerCampBundles, (bundle: any, idx) => {
          const bundleId = bundle.id as string;
          const setInfoState = setInfoStateForBundle(bundleId);
          const getAdditionalInfoByKey =
            getAdditionalInfoByKeyForBundle(bundle);
          const isOutOfStock = _.some(productsOutOfStock, (item) => {
            const outOfStockSKU = _.get(item, "product.sku");
            return outOfStockSKU === bundle.sku;
          });
          const priceDetails = getPriceDetailsForBundle(bundle);

          const clothKey =
            bundle.profile === "Adventure" ? "hoodieSize" : "shirtSize";

          return (
            <Box key={idx}>
              <OrderItemSummerCampRow
                isOutOfStock={isOutOfStock}
                agreementUrl={tripSummer2023TermsURL}
                isEditable={isEditable}
                discount={priceDetails.productDiscounts[0]}
                id={isEditable ? bundle.id : null}
                sport={"Camp 2026"}
                level={bundle.profile}
                location="Star-Dadaj 2026"
                price={
                  isEditable
                    ? priceDetails.priceBeforeDiscountsWithoutModifiers.toFixed(
                        2
                      )
                    : paymentPriceOf(bundle, priceDetails.priceAfterDiscounts)
                }
                name={dynamicName(bundle)}
                periodName={bundle.name || "błąd?"}
                removeFromCart={(id: string) => removeFromCart(id, bundle)}
                //
                setCampConsent={setInfoState("campConsent")}
                campConsent={getAdditionalInfoByKey("campConsent")}
                //
                setInsuranceConsent={setInfoState("insuranceConsent")}
                insuranceConsent={getAdditionalInfoByKey("insuranceConsent")}
                //
                setShirtSize={setInfoState(clothKey)}
                shirtSize={getAdditionalInfoByKey(clothKey)}
                //
                setDiet={setInfoState("diet")}
                diet={getAdditionalInfoByKey("diet")}
                //
                setRoommatesInfo={setInfoState("roommatesInfo")}
                roommatesInfo={getAdditionalInfoByKey("roommatesInfo")}
                //
                setHealthInfo={setInfoState("healthInfo")}
                healthInfo={getAdditionalInfoByKey("healthInfo")}
                //
                setTransport={setInfoState("transport")}
                transport={getAdditionalInfoByKey("transport")}
              />
            </Box>
          );
        })}
      </>
    );

    const DiscountsPart = () => (
      <>
        <Box mb={3}>
          <Text as="b">Zniżki</Text>
        </Box>
        {_.map(discounts, (discount, idx) => (
          <Box key={idx}>
            <OrderItemDiscountRow
              price={discount.discountValue.value}
              name={discount.message}
              type={
                discount.discountValue.__typename === "DiscountPercentage"
                  ? "PERCENTAGE"
                  : "VALUE"
              }
            />
          </Box>
        ))}
      </>
    );

    const shouldRenderMembershipsPart =
      !isEditable && _.size(membershipsBundles) > 0;
    const shouldRenderActivitesPart = _.size(activitiesBundles) > 0;
    const shouldRenderWinterCampsPart = _.size(winterCampBundles) > 0;
    const shouldRenderSummerCampsPart = _.size(summerCampBundles) > 0;
    const shouldRenderDiscounts = !isEditable && _.size(discounts) > 0;

    return (
      <>
        {shouldRenderMembershipsPart && <MembershipPart />}
        {shouldRenderMembershipsPart && <Divider mb={3} />}

        {shouldRenderActivitesPart && <ActivitiesPart />}
        {shouldRenderActivitesPart && <Divider mb={3} />}

        {shouldRenderWinterCampsPart && <WinterCampsPart />}
        {shouldRenderWinterCampsPart && <Divider mb={3} />}

        {shouldRenderSummerCampsPart && <SummerCampsPart />}
        {shouldRenderSummerCampsPart && <Divider mb={3} />}

        {shouldRenderDiscounts && <DiscountsPart />}
        {shouldRenderDiscounts && <Divider mb={3} />}
      </>
    );
  };

  const PaymentAccordion = (paymentOption: any) => {
    const winterIdsInCart = getWinterCampsIDsFromItemGroups(
      itemGroups as CartItemGroup[]
    );

    const summerIdsInCart = getSummerCampsIDsFromItemGroups(
      itemGroups as CartItemGroup[]
    );

    const campsIds = [...winterIdsInCart, ...summerIdsInCart];

    // Transport is mandatory for summer camps and, since 2027, for winter camps
    const missingTransportsForTrips = !checkIfEverybodyHasTransport(
      additionalItemsInfo,
      [
        ...summerIdsInCart,
        ...getWinter2027CampsIDsFromItemGroups(itemGroups as CartItemGroup[]),
      ]
    );
    // Zima 2027 has no shirt size (SCRUM-137)
    const winter2027Ids = getWinter2027CampsIDsFromItemGroups(
      itemGroups as CartItemGroup[]
    );
    const missingShirtSizesForTrips = !checkIfEverybodyHasShirtSize(
      additionalItemsInfo,
      _.difference(campsIds, winter2027Ids)
    );
    const missingInsuranceConsentsForTrips =
      !checkIfEverybodyHasInsuranceConsent(additionalItemsInfo, campsIds);
    const missingCampConsentsForTrips = !checkIfEverybodyHasCampConsent(
      additionalItemsInfo,
      campsIds
    );
    const missingSportForTrips = !checkIfEverybodyHasSport(
      additionalItemsInfo,
      winterIdsInCart
    );
    const missingLevelForTrips = !checkIfEverybodyHasLevel(
      additionalItemsInfo,
      winterIdsInCart
    );

    const activitiesIdsInCart = getActivitiesIDsFromItemGroups(
      itemGroups as CartItemGroup[]
    );
    const missingItemConsentsForActivities =
      !checkIfEverybodyHasActivityConsent(
        additionalItemsInfo,
        activitiesIdsInCart
      );

    // Child profile deleted after its item was added; the backend refuses such an order (SCRUM-134)
    const hasDeletedChildProfile = _.some(
      itemGroups as CartItemGroup[],
      (group) =>
        group.participant?.__typename !== "Account" &&
        _.some(group.problems, ({ code }) => code === "MissingAccountData")
    );

    const disableBuyButtonReasons = _.compact([
      hasDeletedChildProfile
        ? "Profil uczestnika został usunięty po dodaniu produktu do koszyka. Usuń jego pozycje z koszyka lub skontaktuj się z biurem."
        : null,
      missingItemConsentsForActivities
        ? "Niektórzy uczestnicy zajęć nie mają potwierdzenia regulaminów zajęć."
        : null,
      missingCampConsentsForTrips
        ? "Niektórzy uczestnicy wyjazdów nie mają potwierdzenia regulaminów wyjazdów."
        : null,
      missingShirtSizesForTrips
        ? "Niektórzy uczestnicy wyjazdów nie mają określonego rozmiaru koszulki."
        : null,
      missingSportForTrips
        ? "Niektórzy uczestnicy wyjazdów nie mają określonego sportu."
        : null,
      missingLevelForTrips
        ? "Niektórzy uczestnicy wyjazdów nie mają określonego poziomu doświadczenia."
        : null,
      missingInsuranceConsentsForTrips
        ? "Niektórzy uczestnicy wyjazdów nie mają potwierdzenia otrzymania OWU NNW."
        : null,
      missingTransportsForTrips
        ? "Niektórzy uczestnicy wyjazdów nie mają określonego sposobu transportu."
        : null,
      expiredMembershipProblems
        ? "Niektórym uczestnikom wygasł status członkostwa."
        : null,
      missingAccountDataProblems ? "Brakuje danych opiekuna." : null,
      missingChildDataProblems ? "Brakuje danych jednego z dzieci." : null,
      _.size(missingMembershipProblems) > 0
        ? `Brakuje wybranego statusu członkostwa u jednego z uczestników`
        : null,
      _.size(productsOutOfStock) > 0
        ? "Brak miejsc na niektórych wyjazdach w koszyku"
        : null,
      !hasRequiredConsents
        ? "Brakuje wymaganych zgód i potwierdzenia znajomości regulaminów."
        : null,
      !serviceStartConsent
        ? "Brakuje zgody na rozpoczęcie świadczenia usługi przed upływem 14 dni."
        : null,
    ]);
    const disableBuyButton = !_.isEmpty(disableBuyButtonReasons);

    return (
      <>
        <Accordion mt={2} defaultIndex={0}>
          {_.map(paymentOption.payments, (payment, index: number) => {
            const label = payment.name;
            return (
              <AccordionItem key={index}>
                <AccordionButton _expanded={{ bg: "#F0F0F0" }}>
                  <Box flex="1" textAlign="left">
                    <Text as="b" mb={5} size="xxl">
                      {label}
                    </Text>{" "}
                    <Badge mx={isBigScreen ? 5 : 0} px={0}>
                      {payment.dueDate}
                    </Badge>
                    {paymentOption.payments.length > 1 && index === 0 ? (
                      <Badge colorScheme="purple">Aktywna rata</Badge>
                    ) : null}
                  </Box>
                  {paymentOption.payments.length > 1 && <AccordionIcon />}
                </AccordionButton>

                <AccordionPanel pb={4} border={"1px solid #F0F0F0"}>
                  {PickedActivitiesSummary(
                    payment,
                    false,
                    paymentOption.payments
                  )}
                  {paymentOption.payments.length > 1 && (
                    <>
                      <Row>
                        <Col breakPoint={{ sm: 10, md: 10 }}>
                          <Text as="b">
                            Kwota raty {index + 1} z{" "}
                            {paymentOption.payments.length}
                          </Text>
                        </Col>
                        <Col
                          breakPoint={{ sm: 10, md: 2 }}
                          style={{ textAlign: "end" }}
                        >
                          <Text as="b">{payment.price.value} zł</Text>
                        </Col>
                      </Row>

                      <Divider my={3} />
                    </>
                  )}
                  {paymentOption.payments.length === 1 && (
                    <>
                      <Flex>
                        <Box mb={3}>
                          <Text as="b">Kwota do zapłaty</Text>
                        </Box>
                        <Spacer />
                        <Text as="b">{paymentOption.price.value} zł</Text>
                      </Flex>
                      <Divider mb={3} />
                    </>
                  )}
                  <Center>
                    <Checkbox
                      colorScheme="blue"
                      onChange={() => setWantsInvoice(!wantsInvoice)}
                    >
                      <Box pl={2}>
                        {"Chcę otrzymać fakturę VAT do zamówienia"}
                      </Box>
                    </Checkbox>
                  </Center>
                  <Checkbox
                    mt={3}
                    colorScheme="blue"
                    alignItems="flex-start"
                    isChecked={serviceStartConsent}
                    onChange={() =>
                      setServiceStartConsent(!serviceStartConsent)
                    }
                  >
                    <Box pl={2}>
                      {
                        "Wyrażam zgodę na rozpoczęcie świadczenia usługi przed upływem 14 dni od zawarcia umowy i przyjmuję do wiadomości, że w razie odstąpienia zapłacę za usługi już wykonane."
                      }
                    </Box>
                  </Checkbox>
                  <Button
                    isLoading={placeOrderLoading || isPlacingOrder}
                    mt={2}
                    p={2}
                    colorScheme="blue"
                    style={{
                      whiteSpace: "break-spaces",
                      height: "fit-PaymentTabs",
                    }}
                    isDisabled={index !== 0 || disableBuyButton} // FIXMEEEEEE index
                    width={"100%"}
                    onClick={() => {
                      if (placingOrderRef.current) return;
                      placingOrderRef.current = true;
                      setIsPlacingOrder(true);
                      setPaymentActionStarted(true);
                      fetchPayment();
                    }}
                  >
                    Potwierdzam i kupuje z obowiązkiem zapłaty
                  </Button>

                  {disableBuyButton ? (
                    <>
                      <Center fontSize="0.7rem" fontStyle="italic">
                        Powyższy przycisk jest zablokowany, ponieważ:
                      </Center>
                      <Center fontSize="0.7rem" fontStyle="italic">
                        <List>
                          {_.map(disableBuyButtonReasons, (reason) => (
                            <ListItem fontSize={"0.7rem"} key={reason}>
                              - {reason}
                            </ListItem>
                          ))}
                        </List>
                      </Center>
                    </>
                  ) : null}
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </>
    );
  };

  const PaymentTabs = () => {
    // Both camps add 5% to instalments; winter only since 2027
    const hasSummerCampsInCart =
      _.size(getSummerCampsIDsFromItemGroups(itemGroups as CartItemGroup[])) >
        0 ||
      _.size(
        getWinter2027CampsIDsFromItemGroups(itemGroups as CartItemGroup[])
      ) > 0;

    return (
      <Tabs>
        <TabList>
          {_.map(paymentOptions, (paymentOption, idx) => (
            <Tab key={idx} onClick={() => setPaymentIndex(idx)}>
              {paymentOption.name}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {_.map(paymentOptions, (paymentOption: any, idx) => {
            return (
              <TabPanel key={idx} p={2}>
                {paymentOption.payments.length > 1 && hasSummerCampsInCart ? (
                  <Box mb={3}>
                    <Center>
                      <Text fontStyle="italic">
                        {
                          "Przy płatności ratalnej za udział w obozie obowiązuje zwyżka 5% od ceny"
                        }
                      </Text>
                    </Center>
                  </Box>
                ) : undefined}
                {PaymentAccordion(paymentOption)}
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    );
  };

  const PaymentCard = () => (
    <Card>
      <CardBody>
        <Heading
          fontSize={isBigScreen ? "xl" : "lg"}
          padding={0}
          color={"gray.700"}
          mb={3}
        >
          Podsumowanie
        </Heading>
        {loading && <Loading />}
        {error && <GenericErrorAlert />}
        {cart && PaymentTabs()}
      </CardBody>
    </Card>
  );

  const AgreementCard = () => {
    if (loading || error || _.isEmpty(data)) {
      return (
        <Card>
          <CardBody>
            {loading && <Loading />}
            {error && <GenericErrorAlert />}
          </CardBody>
        </Card>
      );
    } else if (data) {
      return (
        <Card>
          <CardBody>
            <Heading
              fontSize={isBigScreen ? "xl" : "lg"}
              padding={0}
              color={"gray.700"}
              mb={3}
            >
              Zgody i regulaminy
            </Heading>
            <Agreements
              requiredConsents={requiredConsents}
              setHasRequiredConsents={setHasRequiredConsents}
              setConsents={setConsents}
              consents={data.me.consents}
            />
          </CardBody>
        </Card>
      );
    }
  };

  const ClientSection = (itemGroup: any) => {
    const missingData = _.some(
      itemGroup.problems,
      ({ code }) => code === "MissingAccountData" || code === "MissingChildData"
    );
    // The child profile was deleted after the item was added (SCRUM-134)
    const deletedChildProblem =
      itemGroup.participant?.__typename !== "Account"
        ? _.find(
            itemGroup.problems,
            ({ code }) => code === "MissingAccountData"
          )
        : undefined;
    return (
      <Box pb={1}>
        <Flex align="center">
          <Box mr={3}>
            {itemGroup.participant?.name && itemGroup.participant?.surname && (
              <Text
                as="b"
                fontSize="xl"
              >{`${itemGroup.participant?.name} ${itemGroup.participant?.surname}`}</Text>
            )}
            {(!itemGroup.participant?.name ||
              !itemGroup.participant?.surname) && (
              <Text as="i">Brak danych uczesnitka</Text>
            )}
          </Box>
          {missingData &&
            TooltipWrapper({ label: missingAccountData, isError: true })}

          <Button
            ml={3}
            size="xs"
            onClick={() => {
              navigate(
                itemGroup.participant.__typename === "Account"
                  ? "/profile/parent"
                  : "/profile/children"
              );
            }}
          >
            Edytuj
          </Button>
        </Flex>
        {deletedChildProblem?.message && (
          <Text color="red.500" fontSize="sm" mt={1}>
            {deletedChildProblem.message}
          </Text>
        )}
      </Box>
    );
  };

  const SingleParticipantCard = (itemGroup: any) => {
    const currentMembershipType = _.get(
      itemGroup,
      "participant.membership.membershipType"
    );
    const hasMembership = !!currentMembershipType;
    const missingAnyMembership = _.some(
      _.get(itemGroup, "problems", []),
      (problem) => {
        const isMissingProduct = problem.code === "MissingProductRequirement";
        const propositionIsMembership = _.some(
          problem.availableProducts,
          ({ name }) => _.startsWith(name, "Członkostwo")
        );
        return isMissingProduct && propositionIsMembership;
      }
    );

    const membershipTypeInCart = _.get(
      _.find(
        itemGroup.bundles,
        ({ __typename }) => __typename === "CartItemMembership"
      ),
      "membershipType"
    );

    const hasMembershipInCart = !!membershipTypeInCart;

    const onSelectMembership = (newType: MembershipType) => {
      if (
        !!meId &&
        (!missingAnyMembership || newType !== membershipTypeInCart)
      ) {
        addMembershipAction(
          meId,
          newType,
          itemGroup.participant?.__typename === "Child"
            ? itemGroup.participant?.id
            : undefined
        );
      }
    };
    // Pokazanie ceny per uczestnik
    const priceBeforeDiscounts = _.reduce(
      itemGroup.bundles,
      (acc, bundle) => {
        const priceDetails = getPriceDetailsForBundle(bundle);
        return acc + priceDetails.priceBeforeDiscountsWithModifiers;
      },
      0
    );
    const discountsAmount = _.reduce(
      itemGroup.bundles,
      (acc, bundle) => {
        const priceDetails = getPriceDetailsForBundle(bundle);
        return acc + priceDetails.discountsAmount;
      },
      0
    );

    return (
      <>
        {ClientSection(itemGroup)}
        <Divider my={2} />
        {MembershipSelectSection({
          isOpen: isMembershipModalOpen,
          isLoading: addMembershipLoading,
          setIsOpen: setIsMembershipModalOpen,
          hasMembership: hasMembership,
          hasMembershipInCart,
          membershipType: currentMembershipType,
          membershipTypeInCart,
          onSelectMembership,
        })}
        <Divider my={2} />
        {PickedActivitiesSummary(itemGroup, true)}
        <Flex>
          <Box mb={3}>
            <Text as="b">Cena</Text>
          </Box>
          <Spacer />
          <Box>
            <Text fontWeight={"bold"} as="p" textAlign={"right"}>
              {_.ceil(priceBeforeDiscounts, 2)} zł
            </Text>
            {discountsAmount > 0 && (
              <Text fontSize="sm" as="p" textAlign={"right"}>
                zniżka -{discountsAmount} zł
              </Text>
            )}
          </Box>
        </Flex>
      </>
    );
  };

  const cartIsEmpty = _.isEmpty(cart) || _.isEmpty(data?.me?.cart?.itemsGroups);
  const { on: showDiscounts } = useFeature("discount-codes");

  return (
    <Layout>
      <Center>
        <Col breakPoint={{ sm: 12, lg: 12, xl: 10, xxl: 9, xxxl: 9 }}>
          <>
            {anyCartLoading || error ? (
              <Card>
                <CardBody>
                  {anyCartLoading && <Loading />}
                  {error && <GenericErrorAlert />}
                </CardBody>
              </Card>
            ) : null}
            {!loading && cartIsEmpty && <EmptyBasket />}

            {isIncomplete && <MissingProfileDataAlert parent={true} />}

            {!loading && !cartIsEmpty && (
              <>
                {_.map(data?.me?.cart?.itemsGroups, (itemGroup, index) => {
                  return (
                    <Card key={index}>
                      <CardBody>{SingleParticipantCard(itemGroup)}</CardBody>
                    </Card>
                  );
                })}
                {AgreementCard()}
                {showDiscounts && (
                  <DiscountCard currentCode={currentDiscountCode} />
                )}
                {PaymentCard()}
              </>
            )}
          </>
        </Col>
      </Center>
    </Layout>
  );
};

export default Cart;
